// @flow
import moment from 'moment';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import orderBy from 'lodash/orderBy';
import { centsToDollars } from 'clark-utils';

import type { SessionType, SessionsType } from 'types/sessions';
import type { EngagementType } from 'types/engagements';
import { getEngagementDetails } from '../engagements';

export const hasSessionReport = ({
  attributes: { sessionReportRating, internalNotes, sessionReportBody },
}: SessionType) => !!(sessionReportRating || internalNotes || sessionReportBody);

export const getSessionDuration = (session: SessionType): moment$MomentDuration => {
  const {
    attributes: { startTime, endTime },
  } = session;
  const sessionStartTime = moment(startTime);
  const sessionEndTime = moment(endTime);
  const sessionDuration = moment.duration(sessionEndTime.diff(sessionStartTime));

  return sessionDuration;
};

export const getSessionDurationInHours = (duration: moment$MomentDuration): number =>
  duration.asHours();

export const getSessionWageInCents = (session: SessionType): number => {
  const sessionDuration = getSessionDuration(session);
  const sessionDurationInHours = getSessionDurationInHours(sessionDuration);
  const {
    attributes: { providerTakeHomeHourlyRateInCents },
  } = session;
  const sessionWageInCents = providerTakeHomeHourlyRateInCents * sessionDurationInHours;

  return sessionWageInCents;
};

export const getSessionWageInDollars = (session: SessionType): number => {
  const sessionWageInCents = getSessionWageInCents(session);
  const sessionWageInDollars = centsToDollars(sessionWageInCents);

  return sessionWageInDollars;
};

export const calculateSessionAdjustedDurationInHours = (
  session: SessionType,
  engagement: EngagementType,
): number => {
  const sessionDuration = getSessionDuration(session);
  const sessionDurationInHours = getSessionDurationInHours(sessionDuration);
  const { sessionLengthInHours } = getEngagementDetails(engagement);
  const differenceInHours = sessionDurationInHours - sessionLengthInHours;
  return Math.round(differenceInHours * 1e2) / 1e2;
};

export const sortedByStartTime = (sessions: SessionsType, groupingId: ?string): SessionType[] =>
  orderBy(
    filter(sessions, s => s.attributes.serviceSessionRecurrenceGroupingId === groupingId),
    'attributes.startTime',
  );

export const calculateSeriesPosition = (session: SessionType, sessions: SessionsType) => {
  const {
    attributes: { serviceSessionRecurrenceGroupingId },
  } = session;
  const sortedRecurringSessions = sortedByStartTime(sessions, serviceSessionRecurrenceGroupingId);
  const index = findIndex(sortedRecurringSessions, s => s.id === session.id);

  return {
    index,
    count: sortedRecurringSessions.length,
  };
};

export const isLastInSeries = (session: SessionType, sessions: SessionsType) => {
  const { index, count } = calculateSeriesPosition(session, sessions);
  return index === count - 1;
};

export const editRecurringEnabled = (
  session: SessionType,
  sessions: SessionsType,
  serviceSessionRecurrenceGroupingId: ?string,
  startTime: string,
) => {
  const now = moment();
  return (
    moment(startTime).isAfter(now) &&
    !!serviceSessionRecurrenceGroupingId &&
    !isLastInSeries(session, sessions)
  );
};

export const getRemainingRecurringSessions = (
  session: SessionType,
  sessions: SessionsType,
): SessionType[] => {
  const {
    attributes: { serviceSessionRecurrenceGroupingId },
  } = session;
  const sortedRecurringSessions = sortedByStartTime(sessions, serviceSessionRecurrenceGroupingId);
  const index = findIndex(sortedRecurringSessions, s => s.id === session.id);

  return sortedRecurringSessions.slice(index);
};

export const formatSeriesItem = ({
  startTime,
  endTime,
}: {
  startTime: string,
  endTime: string,
}) => {
  const formattedSessionDate = moment(startTime).format('ddd, MMM Do');
  const formattedSessionStartTime = moment(startTime).format('h:mm');
  const formattedSessionEndTime = moment(endTime).format('h:mm a');

  return `${formattedSessionDate} ${formattedSessionStartTime} ${formattedSessionEndTime}`;
};



// WEBPACK FOOTER //
// ./src/utils/helpers/sessions/index.js